import {
  ACCESS_TOKEN_STORAGE_KEY,
  AUTH_REFRESH_ENDPOINT,
  ENotificationWsEvent,
  NOTIFICATIONS_WS_NAMESPACE,
} from "@frontend-shared/constants";
import type {
  IMeetingStatusChangedEventPayload,
  INotificationCreatedEventPayload,
  INotificationsWsAuthPayload,
  IOrganizationAccessChangedEventPayload,
  IPresentationExtractionProgressEventPayload,
} from "@frontend-shared/types";
import axios from "axios";
import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";

import { getLanguage, getProjectUuid, getToken } from "./auth";

// NOTE: Isolated Socket.io connection for app notifications. Separate from
// the chat socket so notification flow is independent of the active chat.
let notificationsSocket: Socket | null = null;

// NOTE: Coalesce concurrent /api/auth/refresh POSTs into a single in-flight
// request. Mirrors `WebSocketService.isRefreshing` on the chat socket — if
// socket.io's exponential-backoff retry fires multiple connect_error events
// while a refresh is in flight, we don't want each to POST to /refresh.
let isRefreshing = false;

const buildAuthPayload = (): INotificationsWsAuthPayload => ({
  token: `Bearer ${getToken()}`,
  projectUuid: getProjectUuid(),
  language: getLanguage(),
});

// NOTE: Mirrors `WebSocketService.isAuthError` on the chat socket. The
// server's NotificationsGateway calls `client.disconnect()` after
// `jwtService.verifyAsync` throws, which surfaces here as a connect_error
// carrying either a JsonWebTokenError-shaped error code or a generic
// message string. Match both because the wire shape varies by transport
// and socket.io minor version.
const isAuthError = (error: Error): boolean => {
  const errorWithCode = error as Error & {
    code?: string | number;
    data?: { code?: string | number };
  };
  const code = errorWithCode.code ?? errorWithCode.data?.code;

  if (
    code === 401 ||
    code === "401" ||
    code === "ERR_JWT_EXPIRED" ||
    code === "ERR_INVALID_TOKEN" ||
    code === "ERR_UNAUTHORIZED"
  ) {
    return true;
  }

  const message = (error.message ?? "").toLowerCase();
  const authPatterns = [
    "unauthorized",
    "invalid token",
    "invalid signature",
    "expired token",
    "expired jwt",
    "jwt expired",
    "invalid jwt",
    "authentication failed",
    "not authenticated",
    "session expired",
  ];

  return authPatterns.some((pattern) => message.includes(pattern));
};

// NOTE: POSTs to /api/auth/refresh and writes the new access token back to
// localStorage so the next `buildAuthPayload()` reads it. Mirrors the chat
// WS's handleAuthError refresh body. The `isRefreshing` guard ensures
// concurrent connect_error events coalesce into a single POST.
const handleAuthError = async (): Promise<void> => {
  if (isRefreshing) return;
  isRefreshing = true;
  try {
    const response = await axios.post(AUTH_REFRESH_ENDPOINT);
    // NOTE: `axios.post` returns `AxiosResponse<unknown>` here because the
    // refresh endpoint is hit via the raw axios import (not the typed
    // `authApi` service) — the WS lib needs to stay free of the project's
    // ApiClient instance, which carries an interceptor that would loop
    // through this same refresh path on failure. Narrow the response shape
    // inline; treat anything other than a non-empty string as "refresh did
    // not yield a token" and bail.
    const nextAccessToken = response?.data?.accessToken as string | undefined;
    if (!nextAccessToken) return;
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, nextAccessToken);
    // NOTE: socket.io's internal reconnect uses the `auth` object that was
    // passed to `io()` originally; we MUST mutate `socket.auth` here so the
    // very next reconnect attempt picks up the new token instead of
    // replaying the stale one. AND we have to call `connect()` explicitly:
    // when the server rejects auth it does `client.disconnect()`, which
    // surfaces as a `"io server disconnect"` reason and disables socket.io's
    // automatic reconnection (per docs). Without `connect()` the socket
    // would stay dead for the rest of the session even after a successful
    // refresh, leaving notification delivery broken.
    if (notificationsSocket) {
      notificationsSocket.auth = buildAuthPayload();
      if (!notificationsSocket.connected) {
        notificationsSocket.connect();
      }
    }
  } catch {
    // NOTE: Match the chat WS — log nothing here (the axios interceptor
    // surfaces the auth failure to the rest of the app, which triggers the
    // sign-out flow). Re-throwing would crash the connect_error handler
    // without changing the outcome.
  } finally {
    isRefreshing = false;
  }
};

export const getNotificationsSocket = (): Socket => {
  if (!notificationsSocket) {
    notificationsSocket = io(NOTIFICATIONS_WS_NAMESPACE, {
      auth: buildAuthPayload(),
      transports: ["websocket"],
      autoConnect: false,
      reconnection: true,
      // NOTE: forceNew gives the notifications socket its OWN engine.io
      // transport instead of multiplexing onto the chat WS's shared Manager.
      // Without this, two sockets on the same origin (`/api/chat` + this
      // namespace) share a Manager — and with notifications constructed
      // first (autoConnect: false) the chat namespace's CONNECT packet
      // doesn't fire on the shared transport, leaving the chat WS stuck on
      // "Connecting..." forever and the send button disabled.
      forceNew: true,
    });

    // NOTE: Auth-aware connect_error handler — mirrors WebSocketService on
    // the chat socket. The previous "just re-read getToken()" behaviour
    // hot-loops when the stored token is invalid because every retry re-uses
    // the same bad token. Now on an auth error we POST to /auth/refresh
    // once (coalesced via isRefreshing), write the new token to
    // localStorage, and mutate socket.auth so the next auto-reconnect
    // carries the fresh credential. On a non-auth error (network blip,
    // server restart) we just refresh the auth payload in case a sibling
    // flow has rotated localStorage in the meantime.
    notificationsSocket.on("connect_error", async (error: Error) => {
      if (!notificationsSocket) return;

      if (isAuthError(error)) {
        await handleAuthError();
        return;
      }

      notificationsSocket.auth = buildAuthPayload();
    });
  }
  return notificationsSocket;
};

export const connectNotificationsSocket = (): void => {
  const socket = getNotificationsSocket();
  socket.auth = buildAuthPayload();
  if (!socket.connected) {
    socket.connect();
  }
};

export const disconnectNotificationsSocket = (): void => {
  if (notificationsSocket?.connected) {
    notificationsSocket.disconnect();
  }
};

// NOTE: Whether the notifications socket is currently connected. Reads the
// existing singleton without creating one (so a caller checking "is push
// available?" never spins up a socket as a side effect). Used by the meetings
// query to decide whether it needs a fallback poll — the socket is
// websocket-only, so on networks that block websockets it may never connect.
export const isNotificationsSocketConnected = (): boolean =>
  notificationsSocket?.connected ?? false;

export const onNotificationCreated = (
  callback: (payload: INotificationCreatedEventPayload) => void,
): (() => void) => {
  const socket = getNotificationsSocket();
  socket.on(ENotificationWsEvent.NOTIFICATION_CREATED, callback);
  return () => {
    socket.off(ENotificationWsEvent.NOTIFICATION_CREATED, callback);
  };
};

// NOTE: Live-refresh signal for the call-assistant list — the server pushes
// this on every meeting status transition so the client can invalidate the
// meetings query instead of polling every 10s.
export const onMeetingStatusChanged = (
  callback: (payload: IMeetingStatusChangedEventPayload) => void,
): (() => void) => {
  const socket = getNotificationsSocket();
  socket.on(ENotificationWsEvent.MEETING_STATUS_CHANGED, callback);
  return () => {
    socket.off(ENotificationWsEvent.MEETING_STATUS_CHANGED, callback);
  };
};

// NOTE: Live permission-refresh signal for organization ownership changes
// (NSAI-3616). Separate from the ORGANIZATION_OWNERSHIP_RECEIVED /
// OWNER_REMOVED bell notifications on purpose — those are gated on the user's
// ORGANIZATIONS notification preference, so subscribing to them would leave
// anyone who muted the category rendering owner-gated UI the server no longer
// authorizes. This event is never persisted and never preference-gated.
export const onOrganizationAccessChanged = (
  callback: (payload: IOrganizationAccessChangedEventPayload) => void,
): (() => void) => {
  const socket = getNotificationsSocket();
  socket.on(ENotificationWsEvent.ORGANIZATION_ACCESS_CHANGED, callback);
  return () => {
    socket.off(ENotificationWsEvent.ORGANIZATION_ACCESS_CHANGED, callback);
  };
};

// NOTE: Staged-progress push for the background template extraction job
// (NSAI-3862) — consumers filter by jobId and fall back to polling the job
// status endpoint, so a lost event never strands the upload modal.
export const onPresentationExtractionProgress = (
  callback: (payload: IPresentationExtractionProgressEventPayload) => void,
): (() => void) => {
  const socket = getNotificationsSocket();
  socket.on(ENotificationWsEvent.PRESENTATION_EXTRACTION_PROGRESS, callback);
  return () => {
    socket.off(ENotificationWsEvent.PRESENTATION_EXTRACTION_PROGRESS, callback);
  };
};

// NOTE: Fires on the initial connect AND every socket.io auto-reconnect. Used
// to re-sync state that may have changed while the socket was down — a push
// event delivered during a disconnect is lost, so consumers invalidate their
// queries on reconnect to catch up. Returns an unsubscribe.
export const onNotificationsSocketConnect = (
  callback: () => void,
): (() => void) => {
  const socket = getNotificationsSocket();
  socket.on("connect", callback);
  // NOTE: connectNotificationsSocket() runs before this subscription, and it
  // skips socket.connect() when the singleton is already connected (e.g. a
  // prior mount left it up). In that case the "connect" event has already
  // fired and this listener would never see the initial connect, leaving the
  // consumer's reconnect bookkeeping unset. Invoke once now to cover it.
  if (socket.connected) {
    callback();
  }
  return () => {
    socket.off("connect", callback);
  };
};
